// OneSignal Push Notification Helper
// This module provides easy access to OneSignal functionality

// Track the last set external user ID to detect user changes
let lastExternalUserId = null;

/**
 * Check if OneSignal is available
 */
export const isOneSignalReady = () => {
  return typeof window !== 'undefined' && window.OneSignalDeferred;
};

/**
 * Wait for OneSignal to be fully initialized
 * @param {number} maxRetries - Maximum number of retry attempts
 * @param {number} delay - Delay between retries in ms
 */
const waitForOneSignal = (maxRetries = 10, delay = 500) => {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    
    const checkReady = () => {
      attempts++;
      
      if (isOneSignalReady()) {
        // OneSignal SDK is loaded, now check if it's initialized
        window.OneSignalDeferred.push(async (OneSignal) => {
          try {
            // Try to access a property to confirm initialization
            const isPushSupported = OneSignal.Notifications.isPushSupported();
            console.log('[OneSignal] SDK initialized, push supported:', isPushSupported);
            resolve(OneSignal);
          } catch (error) {
            if (attempts < maxRetries) {
              console.log(`[OneSignal] Not ready yet, retry ${attempts}/${maxRetries}`);
              setTimeout(checkReady, delay);
            } else {
              reject(new Error('OneSignal initialization timeout'));
            }
          }
        });
      } else {
        if (attempts < maxRetries) {
          console.log(`[OneSignal] SDK not loaded, retry ${attempts}/${maxRetries}`);
          setTimeout(checkReady, delay);
        } else {
          reject(new Error('OneSignal SDK not available'));
        }
      }
    };
    
    checkReady();
  });
};

/**
 * Request push notification permission
 * Shows the native browser prompt if not already granted.
 * Falls back to the native `Notification.requestPermission` if
 * OneSignal isn't ready (e.g. Safari, PWA offline, blocked SDK).
 * Returns 'granted' | 'denied' | 'unsupported' | false.
 */
export const requestNotificationPermission = async () => {
  // Fast path: browser refuses notifications entirely (Safari <16.4 iOS
  // non-PWA, embedded WebViews, etc.). Surface it so callers can guide
  // the user to Settings.
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    console.warn('[Notifications] Notification API unsupported on this browser');
    return 'unsupported';
  }

  // Already granted at OS level? Nothing to do — resolve immediately.
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied')  return 'denied';

  // Prefer OneSignal (tracks subscription, wires up service worker)
  // but if it's not ready within ~2s, fall back to the native prompt
  // so the button always feels responsive.
  const requestViaOneSignal = () => new Promise((resolve) => {
    if (!isOneSignalReady()) return resolve(null);
    let settled = false;
    const finish = (v) => { if (!settled) { settled = true; resolve(v); } };
    // Hard timeout so the click never hangs forever
    setTimeout(() => finish(null), 2500);
    try {
      window.OneSignalDeferred.push(async (OneSignal) => {
        try {
          await OneSignal.Notifications.requestPermission();
          const p = await OneSignal.Notifications.permission;
          finish(p === true || p === 'granted' ? 'granted' : (p === false || p === 'denied' ? 'denied' : null));
        } catch (err) {
          console.warn('[OneSignal] requestPermission threw, falling back:', err);
          finish(null);
        }
      });
    } catch (err) {
      console.warn('[OneSignal] Deferred push threw, falling back:', err);
      finish(null);
    }
  });

  const osResult = await requestViaOneSignal();
  if (osResult) return osResult;

  // Native fallback — always fires the browser prompt
  try {
    const perm = await Notification.requestPermission();
    return perm;   // 'granted' | 'denied' | 'default'
  } catch (err) {
    console.error('[Notifications] Native requestPermission failed:', err);
    return false;
  }
};

/**
 * Check current notification permission status
 */
export const getNotificationPermission = async () => {
  if (!isOneSignalReady()) return 'default';

  return new Promise((resolve) => {
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        const permission = await OneSignal.Notifications.permission;
        resolve(permission ? 'granted' : 'default');
      } catch (error) {
        resolve('default');
      }
    });
  });
};

/**
 * Check if user is subscribed to push notifications
 */
export const isPushSubscribed = async () => {
  if (!isOneSignalReady()) return false;

  return new Promise((resolve) => {
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        const isPushSupported = OneSignal.Notifications.isPushSupported();
        if (!isPushSupported) {
          resolve(false);
          return;
        }
        
        const subscription = await OneSignal.User.PushSubscription.optedIn;
        resolve(subscription);
      } catch (error) {
        console.error('[OneSignal] Subscription check failed:', error);
        resolve(false);
      }
    });
  });
};

/**
 * Link OneSignal subscription with Supabase user
 * 
 * This function:
 * 1. Calls OneSignal.login(userId) to set external_user_id = Supabase auth user.id
 * 2. Calls OneSignal.User.addEmail(email) to add email to the profile
 * 3. Calls OneSignal.User.addTags({ user_id: userId }) for segmentation
 * 
 * @param {string} userId - Supabase auth user.id (UUID)
 * @param {string} userEmail - The user's email address
 * @param {number} retryCount - Current retry attempt (internal use)
 * @returns {Promise<boolean>} - Whether the operation succeeded
 */
export const linkOneSignalUser = async (userId, userEmail, retryCount = 0) => {
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000;

  if (!userId) {
    console.error('[OneSignal] ERROR: Cannot set user - userId is required');
    return false;
  }

  // Check if this is the same user - no need to update
  if (lastExternalUserId === userId) {
    console.log('[OneSignal] User already linked:', userId);
    return true;
  }

  try {
    console.log('[OneSignal] Initializing SDK...');
    await waitForOneSignal();
    
    return new Promise((resolve) => {
      window.OneSignalDeferred.push(async (OneSignal) => {
        try {
          const permission = await OneSignal.Notifications.permission;
          console.log('[OneSignal] Permission status:', permission ? 'granted' : 'not granted');
          
          if (!permission) {
            console.log('[OneSignal] Waiting for permission - storing userId for later:', userId);
            localStorage.setItem('pendingOneSignalUserId', userId);
            if (userEmail) localStorage.setItem('pendingOneSignalEmail', userEmail);
            resolve(false);
            return;
          }

          // If a different user was logged in before, logout first
          if (lastExternalUserId && lastExternalUserId !== userId) {
            console.log('[OneSignal] Switching user from:', lastExternalUserId, 'to:', userId);
            try {
              await OneSignal.logout();
              console.log('[OneSignal] Previous user logged out');
            } catch (logoutError) {
              console.error('[OneSignal] Logout error:', logoutError.message);
            }
          }

          // Step 1: Set external_user_id using login(userId) — this is the Supabase user.id
          console.log('[OneSignal] Step 1: Calling login() with userId:', userId);
          try {
            await OneSignal.login(userId);
            console.log('[OneSignal] SUCCESS: login() completed — external_user_id =', userId);
          } catch (loginError) {
            console.error('[OneSignal] ERROR: login() failed:', loginError.message);
            throw loginError;
          }

          // Step 2: Add email to OneSignal user profile
          if (userEmail) {
            console.log('[OneSignal] Step 2: Calling User.addEmail() with:', userEmail);
            try {
              await OneSignal.User.addEmail(userEmail);
              console.log('[OneSignal] SUCCESS: addEmail() completed for:', userEmail);
            } catch (emailError) {
              console.error('[OneSignal] ERROR: addEmail() failed:', emailError.message);
            }
          }
          
          // Step 3: Add user_id tag for segmentation
          console.log('[OneSignal] Step 3: Adding user_id tag');
          try {
            await OneSignal.User.addTags({ user_id: userId });
            console.log('[OneSignal] SUCCESS: user_id tag added');
          } catch (tagError) {
            console.error('[OneSignal] ERROR: addTags() failed:', tagError.message);
          }
          
          // Update tracking
          lastExternalUserId = userId;
          localStorage.removeItem('pendingOneSignalUserId');
          localStorage.removeItem('pendingOneSignalEmail');
          
          console.log('[OneSignal] User fully linked — external_user_id:', userId, '| email:', userEmail);
          resolve(true);
          
        } catch (error) {
          console.error('[OneSignal] ERROR linking user:', error.message);
          
          if (retryCount < MAX_RETRIES) {
            console.log(`[OneSignal] Retrying in ${RETRY_DELAY}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);
            setTimeout(async () => {
              const result = await linkOneSignalUser(userId, userEmail, retryCount + 1);
              resolve(result);
            }, RETRY_DELAY);
          } else {
            console.error('[OneSignal] FAILED after', MAX_RETRIES, 'attempts for:', userId);
            resolve(false);
          }
        }
      });
    });
  } catch (error) {
    console.error('[OneSignal] SDK initialization error:', error.message);
    
    if (retryCount < MAX_RETRIES) {
      console.log(`[OneSignal] Retrying initialization in ${RETRY_DELAY}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);
      await new Promise(r => setTimeout(r, RETRY_DELAY));
      return linkOneSignalUser(userId, userEmail, retryCount + 1);
    }
    
    console.error('[OneSignal] FAILED to initialize after', MAX_RETRIES, 'attempts');
    return false;
  }
};

/**
 * Backward-compatible aliases
 * @deprecated Use linkOneSignalUser(userId, email) instead
 */
export const setExternalUserIdByEmail = async (email) => {
  // Legacy: if called with just email, use email as both ID and email
  return linkOneSignalUser(email, email);
};

export const setExternalUserId = async (userId) => {
  return linkOneSignalUser(userId);
};

/**
 * Logout the current user from OneSignal
 * Call this when user logs out of the app
 */
export const logoutOneSignalUser = async () => {
  if (!isOneSignalReady()) return;

  return new Promise((resolve) => {
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        await OneSignal.logout();
        lastExternalUserId = null;
        console.log('[OneSignal] User logged out successfully');
        resolve(true);
      } catch (error) {
        console.error('[OneSignal] Logout error:', error.message);
        resolve(false);
      }
    });
  });
};

/**
 * Process any pending user linking after permission is granted
 */
export const processPendingExternalUserId = async () => {
  const pendingUserId = localStorage.getItem('pendingOneSignalUserId');
  const pendingEmail = localStorage.getItem('pendingOneSignalEmail');
  if (pendingUserId) {
    console.log('[OneSignal] Processing pending user link — userId:', pendingUserId);
    return linkOneSignalUser(pendingUserId, pendingEmail);
  }
  return false;
};

/**
 * Add tags for segmentation
 * @param {object} tags - Key-value pairs for tags
 */
export const addTags = async (tags) => {
  if (!isOneSignalReady() || !tags) return;

  window.OneSignalDeferred.push(async (OneSignal) => {
    try {
      await OneSignal.User.addTags(tags);
      console.log('[OneSignal] Tags added:', tags);
    } catch (error) {
      console.error('[OneSignal] Failed to add tags:', error);
    }
  });
};

/**
 * Show the OneSignal slide prompt (soft prompt)
 */
export const showSlidePrompt = async () => {
  if (!isOneSignalReady()) return;

  window.OneSignalDeferred.push(async (OneSignal) => {
    try {
      await OneSignal.Slidedown.promptPush();
    } catch (error) {
      console.error('[OneSignal] Slidedown prompt failed:', error);
    }
  });
};


/**
 * Get the current OneSignal subscription/player ID.
 * Returns null if not available.
 */
export const getSubscriptionId = async () => {
  if (!isOneSignalReady()) return null;

  return new Promise((resolve) => {
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        const subId = OneSignal.User.PushSubscription.id;
        console.log('[OneSignal] Subscription ID:', subId || 'not available');
        resolve(subId || null);
      } catch (error) {
        console.error('[OneSignal] Failed to get subscription ID:', error);
        resolve(null);
      }
    });
  });
};

/**
 * Theo Phase 4B — register the current OneSignal subscription with the Theo
 * backend so the daily-brief worker can target this device.
 *
 * Flow:
 *   1. Wait for OneSignal SDK
 *   2. Request push permission (no-op if already granted)
 *   3. Read PushSubscription.id (the OneSignal player_id)
 *   4. Link this player to the Supabase user via login(userId)
 *   5. POST to /api/theo/notifications/register-device
 *
 * Returns { ok, playerId, reason }.
 */
export const registerTheoDevice = async (userId, { sendHourLocal = 8 } = {}) => {
  if (!userId) return { ok: false, reason: "no_user_id" };
  try {
    await waitForOneSignal();
  } catch (e) {
    return { ok: false, reason: "onesignal_unavailable" };
  }

  // Step 1 — permission
  const permission = await requestNotificationPermission();
  if (!permission) return { ok: false, reason: "permission_denied" };

  // Step 2 — link user (idempotent)
  try { await linkOneSignalUser(userId); } catch (_) { /* non-fatal */ }

  // Step 3 — read player_id (may need a brief poll after subscribe)
  let playerId = null;
  for (let attempt = 0; attempt < 8 && !playerId; attempt++) {
    playerId = await getSubscriptionId();
    if (!playerId) await new Promise(r => setTimeout(r, 400));
  }
  if (!playerId) return { ok: false, reason: "no_player_id" };

  // Step 4 — best-effort timezone
  const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone) || "UTC";

  // Step 5 — backend register
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  try {
    const resp = await fetch(`${backendUrl}/api/theo/notifications/register-device`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: userId,
        onesignal_player_id: playerId,
        timezone: tz,
        send_hour_local: sendHourLocal,
      }),
    });
    if (!resp.ok) {
      const txt = await resp.text();
      return { ok: false, reason: `backend_${resp.status}`, detail: txt.slice(0, 240) };
    }
    return { ok: true, playerId, timezone: tz };
  } catch (e) {
    return { ok: false, reason: "backend_unreachable", detail: e.message };
  }
};

/**
 * Phase 4B — request an immediate test push for the current user.
 */
export const sendTheoTestBrief = async (userId) => {
  if (!userId) return { ok: false, reason: "no_user_id" };
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  try {
    const resp = await fetch(`${backendUrl}/api/theo/notifications/test-send`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId, force: true }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) return { ok: false, reason: `backend_${resp.status}`, detail: data };
    return { ok: true, results: data.results || [] };
  } catch (e) {
    return { ok: false, reason: "backend_unreachable", detail: e.message };
  }
};
export const registerPlayerWithBackend = async (userId, email) => {
  if (!userId) return false;

  const playerId = await getSubscriptionId();
  if (!playerId) {
    console.log('[OneSignal] No subscription ID — cannot register player');
    return false;
  }

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  try {
    const resp = await fetch(`${backendUrl}/api/notifications/register-player`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, player_id: playerId, email }),
    });
    const data = await resp.json();
    console.log('[OneSignal] Player registered with backend:', data);
    return true;
  } catch (error) {
    console.error('[OneSignal] Backend registration failed:', error);
    return false;
  }
};
